import React, { useEffect, useState } from 'react';
import { Page } from '../../layout/Page';
import { useOrgName, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { Alert, ErrorState, useToast } from '../../components/shared';
import { Dialog } from '../../components/shared/Overlay';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { RETENTION_OPTIONS, accuracySentence, mostAccurateProvider, providerBadges } from './adminModel';
import { useAdminData } from './useAdminData';
import './admin.css';

const LANGUAGES = [
  { code: 'en-US', label: 'English' },
  { code: 'es-US', label: 'Spanish' },
];

// Endpoints, Azure regions and API keys are host configuration (G1 review, G2 close): the page shows the provider, model and deployment only.
const STT = (org, phraseCount, terms) => [
  {
    value: 'AzureAiSpeech',
    name: 'Azure AI Speech',
    description: `Microsoft's batch transcription with speaker separation. Runs in Vertolytics' Azure region; ${phraseCount ? `phrase list includes ${phraseCount} ${terms.communityLower} names` : `the phrase list holds ${terms.communityLower} and street names`}.`,
    badges: [['About $0.18 per hour'], ['English, Spanish']],
  },
  {
    value: 'LocalWhisper',
    name: 'Local Whisper',
    description: "Open-source Whisper large-v3 with speaker separation, running on Vertolytics' own GPU worker. Audio never leaves Vertolytics.",
    badges: [['About $0.02 per hour'], ['English, Spanish']],
  },
  {
    value: 'TeamsNative',
    name: 'Teams transcripts',
    description: 'Uses the transcript Teams already made. No extra cost, but only for calls where Teams transcription is on; English only today.',
    badges: [['No extra cost'], ['English']],
  },
];

const LLM = (org, model) => [
  {
    value: 'Claude',
    name: 'Claude',
    description: `Anthropic API${model ? `, model ${model}` : ''}.`,
    badges: [['About $0.016 per call'], ['No training on data']],
  },
  {
    value: 'AzureOpenAi',
    name: 'Azure OpenAI',
    description: `GPT model deployed in ${org}'s Azure subscription. Data stays in the Azure tenant.`,
    badges: [['About $0.006 per call']],
  },
  {
    value: 'Local',
    name: 'Local model',
    description: "Open-weight model served by Ollama on Vertolytics' GPU worker. Lowest cost per call; weaker on entity extraction.",
    badges: [['Fixed monthly cost']],
  },
];

// Shown only when the host allows the synthetic (demo data) providers (GET settings/ai/status lists them) or one is selected.
const SYNTHETIC_STT = {
  value: 'Synthetic',
  name: 'Synthetic (demo data)',
  description: "Generated demo conversations built from each real call's details. No audio is read and there is no recording to play. Use it to try transcripts and analysis before recording consent.",
  badges: [['Demo data'], ['English, Spanish']],
};

const SYNTHETIC_LLM = {
  value: 'Synthetic',
  name: 'Synthetic (demo data)',
  description: 'A deterministic demo analyzer for synthetic transcripts. No API key or model is needed; every value still carries a verified quote.',
  badges: [['Demo data']],
};

const withSynthetic = (options, statuses, selected, synthetic) =>
  (statuses || []).some((s) => s.provider === 'Synthetic') || selected === 'Synthetic' ? [...options, synthetic] : options;

/** Adds the live status chip and measured accuracy (GET settings/ai/status) to each provider card. */
function withStatus(options, statuses, org, { accuracy = false } = {}) {
  const byProvider = Object.fromEntries((statuses || []).map((s) => [s.provider, s]));
  const best = accuracy ? mostAccurateProvider(statuses) : null;
  return options.map((p) => {
    const status = byProvider[p.value];
    const isBest = best === p.value;
    return {
      ...p,
      description: isBest ? `${p.description} ${accuracySentence(org, status.accuracy)}` : p.description,
      badges: providerBadges(p.badges, status, { showAccuracy: accuracy, best: isBest }),
    };
  });
}

function ProviderCards({ name, options, value, onChange }) {
  return (
    <div className="prov" role="radiogroup" aria-label={name}>
      {options.map((p) => (
        <label key={p.value}>
          <input type="radio" name={name} checked={value === p.value} onChange={() => onChange(p.value)} />
          <span className="pt">
            <span className="rd" />
            {p.name}
          </span>
          <span className="pd">{p.description}</span>
          <span className="pm">
            {p.badges.map(([text, tone, icon]) => (
              <span className={`badge ${tone || ''}`.trim()} key={text}>
                {icon && <Icon name={icon} />}
                {text}
              </span>
            ))}
          </span>
        </label>
      ))}
    </div>
  );
}

function SetRow({ label, description, children }) {
  return (
    <div className="setrow">
      <div>
        <div className="sl">{label}</div>
        <div className="sd">{description}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

const transcriptionOptionsFor = (settings, provider) =>
  provider === settings.transcriptionProvider ? settings.transcriptionOptions || {} : (settings.transcriptionOptionsByProvider || {})[provider] || {};

export function AdminAiPage() {
  const org = useOrgName();
  const terms = useTerms();
  const toast = useToast();
  const { data, loading, error, retry } = useAdminData({ settings: adminAPI.getAiSettings });
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [status, setStatus] = useState(null);
  const [purging, setPurging] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminAPI.getAiStatus().then(
      (result) => !cancelled && setStatus(result),
      () => !cancelled && setStatus(null)
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (data) {
      setSaved(data.settings);
      setForm(data.settings);
    }
  }, [data]);

  const subtitle = `How ${org}'s calls are transcribed and analyzed. Applies to new calls; past calls keep their results unless re-analyzed.`;

  if (error) {
    return (
      <Page title="AI and transcription" subtitle={subtitle}>
        <ErrorState
          title="AI settings couldn't load"
          body={`Vertolytics couldn't read ${org}'s AI configuration. Transcription and analysis keep running with the last saved settings.`}
          error={error}
          onRetry={retry}
        />
      </Page>
    );
  }

  if (loading || !form) {
    return (
      <Page title="AI and transcription" subtitle={subtitle}>
        <div className="stack-lg">
          {['Speech to text', 'Analysis model', 'Languages, privacy, and retention'].map((t) => (
            <section className="card is-loading" key={t}>
              <div className="card-head">
                <div>
                  <div className="t">{t}</div>
                </div>
              </div>
              <div className="card-body">
                <div style={{ height: 150 }} />
              </div>
            </section>
          ))}
        </div>
      </Page>
    );
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const stt = form.transcriptionProvider;
  const sttOptions = transcriptionOptionsFor(form, stt);
  const setSttOption = (patch) =>
    setForm((f) => {
      if (f.transcriptionProvider === stt) return { ...f, transcriptionOptions: { ...(f.transcriptionOptions || {}), ...patch } };
      return { ...f, transcriptionOptionsByProvider: { ...(f.transcriptionOptionsByProvider || {}), [stt]: { ...sttOptions, ...patch } } };
    });

  const chooseStt = (provider) =>
    setForm((f) => {
      if (provider === f.transcriptionProvider) return f;
      const byProvider = { ...(f.transcriptionOptionsByProvider || {}) };
      const shared = (f.transcriptionOptions || {}).phraseList || [];
      byProvider[f.transcriptionProvider] = { ...(f.transcriptionOptions || {}), phraseList: [] };
      const incoming = { diarizationEnabled: true, maxSpeakers: 2, ...(byProvider[provider] || {}), phraseList: shared };
      delete byProvider[provider];
      return { ...f, transcriptionProvider: provider, transcriptionOptions: incoming, transcriptionOptionsByProvider: byProvider };
    });

  const toggleLanguage = (code, on) => set({ languages: on ? [...new Set([...(form.languages || []), code])] : (form.languages || []).filter((l) => l !== code) });
  const threshold = Math.round((form.languageConfidenceThreshold ?? 0.8) * 100);

  const syntheticAvailable = (status?.transcription || []).some((s) => s.provider === 'Synthetic') || stt === 'Synthetic' || form.languageModelProvider === 'Synthetic';

  const deleteSynthetic = async () => {
    setConfirmPurge(false);
    setPurging(true);
    try {
      const result = await adminAPI.deleteSyntheticContent();
      toast(`Deleted ${result.transcripts} synthetic transcripts and ${result.analyses} analyses${result.attributions ? `; ${result.attributions} calls no longer attributed from them` : ''}`);
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setPurging(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const result = await adminAPI.saveAiSettings(form);
      setSaved(result);
      setForm(result);
      toast('Saved. New calls use these settings.');
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="AI and transcription" subtitle={subtitle}>
      <div className="stack-lg">
        <section className="card">
          <div className="card-head">
            <div>
              <div className="t">Speech to text</div>
              <div className="d">Turns each recording into a transcript with speakers. Audio is processed in temporary storage and deleted.</div>
            </div>
          </div>
          <div className="card-body">
            <ProviderCards
              name="stt"
              options={withStatus(withSynthetic(STT(org, ((form.transcriptionOptions || {}).phraseList || []).length, terms), status?.transcription, stt, SYNTHETIC_STT), status?.transcription, org)}
              value={stt}
              onChange={chooseStt}
            />
          </div>
        </section>
        <section className="card">
          <div className="card-head">
            <div>
              <div className="t">Analysis model</div>
              <div className="d">Reads the transcript and produces the summary, classifications, entities, sentiment, and action items, each with a quote as evidence.</div>
            </div>
          </div>
          <div className="card-body">
            <ProviderCards
              name="llm"
              options={withStatus(
                withSynthetic(LLM(org, form.languageModelProvider === 'Claude' ? saved?.languageModel : ''), status?.languageModels, form.languageModelProvider, SYNTHETIC_LLM),
                status?.languageModels,
                org,
                { accuracy: true }
              )}
              value={form.languageModelProvider}
              onChange={(value) => set(value === 'Synthetic' ? { languageModelProvider: value, languageModel: 'synthetic-v1' } : { languageModelProvider: value })}
            />
          </div>
        </section>
        <section className="card">
          <div className="card-head">
            <div>
              <div className="t">Languages, privacy, and retention</div>
            </div>
          </div>
          <div className="card-body">
            <SetRow label="Languages" description="Calls are transcribed in the language spoken. Analysis is always written in English.">
              <div className="row-wrap" style={{ gap: 16 }}>
                {LANGUAGES.map((l) => (
                  <label className="check" key={l.code}>
                    <input type="checkbox" checked={(form.languages || []).includes(l.code)} onChange={(e) => toggleLanguage(l.code, e.target.checked)} />
                    {l.label}
                  </label>
                ))}
              </div>
              <div className="field mt-3">
                <label className="label" htmlFor="th">
                  Ask the model to confirm the language when speech identification is below
                </label>
                <div className="row">
                  <input
                    type="range"
                    id="th"
                    min="50"
                    max="95"
                    value={threshold}
                    style={{ flex: 1, accentColor: 'var(--accent)' }}
                    onChange={(e) => set({ languageConfidenceThreshold: Number(e.target.value) / 100 })}
                  />
                  <span className="num strong" style={{ width: 44 }}>
                    {threshold}%
                  </span>
                </div>
                <span className="hint">Detected language and how it was detected are stored as separate fields on every call.</span>
              </div>
            </SetRow>
            <SetRow label="Redaction" description="Applied to the transcript before it is saved and before analysis.">
              <div className="stack" style={{ gap: 8 }}>
                {['Payment card numbers', 'Bank account and routing numbers', 'Social Security numbers'].map((t) => (
                  <label className="check" key={t}>
                    <input type="checkbox" checked disabled readOnly />
                    {t}
                  </label>
                ))}
                <p className="caption">Always on. Names, street addresses, and phone numbers are kept so calls can be attributed to owners and {terms.communitiesLower}.</p>
              </div>
            </SetRow>
            <SetRow label="Retention" description="Transcripts and analysis are deleted after this period. Recordings are never stored.">
              <select className="select" style={{ maxWidth: 220 }} value={form.retentionDays} onChange={(e) => set({ retentionDays: Number(e.target.value) })} aria-label="Retention">
                {RETENTION_OPTIONS.map((o) => (
                  <option key={o.days} value={o.days}>
                    {o.label}
                  </option>
                ))}
                {!RETENTION_OPTIONS.some((o) => o.days === form.retentionDays) && <option value={form.retentionDays}>{form.retentionDays} days</option>}
              </select>
              <p className="caption mt-2">Records rules for associations vary by state, and some call records may count as official records. Check with counsel before shortening.</p>
            </SetRow>
            <SetRow label="Processing" description="">
              <div className="stack" style={{ gap: 10 }}>
                <label className="switch">
                  <input type="checkbox" checked={Boolean(form.transcriptionEnabled)} onChange={(e) => set({ transcriptionEnabled: e.target.checked })} />
                  Transcribe new calls automatically
                </label>
                <label className="switch">
                  <input type="checkbox" checked={Boolean(form.analysisEnabled)} onChange={(e) => set({ analysisEnabled: e.target.checked })} />
                  Analyze transcripts with AI automatically
                </label>
                <label className="switch">
                  <input type="checkbox" checked={form.sendLowConfidenceToReview} onChange={(e) => set({ sendLowConfidenceToReview: e.target.checked })} />
                  Send Immediate and low-confidence results to the review list
                </label>
                <label className="switch">
                  <input type="checkbox" checked={form.teamsTranscriptsOnly} onChange={(e) => set({ teamsTranscriptsOnly: e.target.checked })} />
                  Analyze calls without a recording using Teams transcripts only
                </label>
                <p className="caption">New calls are usually analyzed 15 to 30 minutes after they end.</p>
              </div>
            </SetRow>
            <SetRow label="Models" description="The model each selected provider uses. Endpoints, Azure regions and API keys are set by Vertolytics for this deployment.">
              <div className="form-grid">
                <div className="field">
                  <label className="label" htmlFor="stt-model">
                    Speech to text model
                  </label>
                  <input className="input" id="stt-model" value={sttOptions.model || ''} placeholder="Provider default" onChange={(e) => setSttOption({ model: e.target.value || null })} disabled={stt === 'TeamsNative'} />
                  <span className="hint">{stt === 'TeamsNative' ? 'Teams transcripts have no model choice.' : 'Leave blank for the provider default.'}</span>
                </div>
                <div className="field">
                  <label className="label" htmlFor="llm-model">
                    Analysis model
                  </label>
                  <input className="input" id="llm-model" value={form.languageModel || ''} placeholder="Model name" onChange={(e) => set({ languageModel: e.target.value })} />
                  <span className="hint">Needed before calls are analyzed.</span>
                </div>
                {form.languageModelProvider === 'AzureOpenAi' && (
                  <div className="field">
                    <label className="label" htmlFor="llm-deployment">
                      Azure OpenAI deployment
                    </label>
                    <input
                      className="input"
                      id="llm-deployment"
                      value={(form.languageModelOptions || {}).deploymentName || ''}
                      onChange={(e) => set({ languageModelOptions: { ...(form.languageModelOptions || {}), deploymentName: e.target.value || null } })}
                    />
                  </div>
                )}
              </div>
            </SetRow>
            {syntheticAvailable && (
              <SetRow label="Synthetic content" description="Transcripts and analyses made by the synthetic (demo data) providers. Deleting them returns those calls to Not analyzed; real transcripts are kept.">
                <button className="btn btn-sm" type="button" onClick={() => setConfirmPurge(true)} disabled={purging}>
                  <Icon name="trash" className="ico-sm" />
                  {purging ? 'Deleting…' : 'Delete synthetic content'}
                </button>
              </SetRow>
            )}
            <SetRow label="Provider data use" description="">
              <div className="alert">
                <Icon name="shield" />
                <div>Each provider is configured under terms that prohibit training on, or keeping, {org}'s call content. Vertolytics doesn't use call content for any other purpose.</div>
              </div>
            </SetRow>
          </div>
        </section>
        <Dialog
          open={confirmPurge}
          onClose={() => setConfirmPurge(false)}
          title="Delete synthetic content?"
          footer={
            <>
              <button className="btn" type="button" onClick={() => setConfirmPurge(false)}>
                Cancel
              </button>
              <button className="btn btn-danger" type="button" onClick={deleteSynthetic}>
                Delete synthetic content
              </button>
            </>
          }
        >
          <p className="small">Deletes every transcript and analysis the synthetic (demo data) providers made for {org}. Those calls show as not analyzed again, and any {terms.communityLower} they set from the caller's words is removed. Real transcripts and analyses aren't affected.</p>
        </Dialog>
        {problem && <Alert tone="error" title="Settings weren't saved">{problem}</Alert>}
        {dirty && (
          <div className="savebar">
            <span className="small strong grow">You have unsaved changes</span>
            <button
              className="btn"
              type="button"
              onClick={() => {
                setForm(saved);
                setProblem(null);
              }}
              disabled={busy}
            >
              Discard
            </button>
            <button className="btn btn-primary" type="button" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        )}
      </div>
    </Page>
  );
}
